'use client'

import { format } from 'date-fns'
import { cs } from 'date-fns/locale'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useReducer, useState } from 'react'
import TextInput from '../../../primitives/components/text-input'
import {
    BoolFilterOptions,
    NumberFilterOptions,
    StringFilterOptions,
    DateFilterOptions,
} from '../../../enums/filter-operators'
import useUpdateEffect from '../../../hooks/update-effect'
import { faRemove } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as Popover from '@radix-ui/react-popover'
import { IconButton, Select } from '@radix-ui/themes'
import { TableColumn, TFilter } from '../../../table/types'

const DateRangePicker = dynamic(
    () => import('react-date-range').then(mod => mod.DateRangePicker),
    { ssr: false }
)

const Operators = {
    text: Object.values(StringFilterOptions),
    number: Object.values(NumberFilterOptions),
    boolean: Object.values(BoolFilterOptions),
    date: Object.values(DateFilterOptions),
} as const

type FilterOperators = keyof typeof Operators

type TRenderValueFieldProps = {
    selectedFilterType: FilterOperators
    filterInternal: Partial<TFilter>
    setFilterInternal: (filter: Partial<TFilter>) => void
}

const RenderValueField = ({
    selectedFilterType,
    filterInternal,
    setFilterInternal,
}: TRenderValueFieldProps) => {
    const t = useTranslations('table')

    switch (selectedFilterType) {
        case 'boolean': {
            return (
                // the backend casts the value to the column type, so send a real boolean
                // (the translated "ANO"/"NE" labels failed the ::boolean cast)
                <Select.Root
                    onValueChange={value => setFilterInternal({ value: value === 'true' })}
                    value={
                        filterInternal.value != null && filterInternal.value !== ''
                            ? filterInternal.value.toString()
                            : ''
                    }
                >
                    <Select.Trigger />
                    <Select.Content>
                        <Select.Item value="true">{t('yes').toUpperCase()}</Select.Item>
                        <Select.Item value="false">{t('no').toUpperCase()}</Select.Item>
                    </Select.Content>
                </Select.Root>
            )
        }
        case 'date': {
            if (filterInternal.operator == 'between') {
                // the value is a [from, to] pair only once a range was picked
                const range = Array.isArray(filterInternal.value) && filterInternal.value.length == 2
                    ? (filterInternal.value as (string | Date)[]).map(v => new Date(v))
                    : undefined

                return (
                    <Popover.Root>
                        <Popover.Trigger>
                            <input
                                readOnly
                                value={range ? range.map(v => format(v, 'dd.MM.yyyy')).join(' - ') : ''}
                            />
                        </Popover.Trigger>
                        <Popover.Anchor />
                        <Popover.Portal>
                            <Popover.Content>
                                <DateRangePicker
                                    months={2}
                                    direction="horizontal"
                                    locale={cs}
                                    showMonthAndYearPickers={false}
                                    ranges={[
                                        {
                                            startDate: range?.[0] ?? new Date(),
                                            endDate: range?.[1] ?? new Date(),
                                        },
                                    ]}
                                    staticRanges={[]}
                                    inputRanges={[]}
                                    onPreviewChange={() => {}}
                                    weekdayDisplayFormat="EEEEE"
                                    monthDisplayFormat="LLLL"
                                    onChange={e => {
                                        setFilterInternal({
                                            value: [
                                                e.range1.startDate ?? new Date(),
                                                e.range1.endDate ?? new Date(),
                                            ],
                                        })
                                    }}
                                />
                            </Popover.Content>
                        </Popover.Portal>
                    </Popover.Root>
                )
            }

            return (
                <TextInput
                    defaultValue={filterInternal.value?.toString()}
                    onValueChange={value => setFilterInternal({ value: value ?? '' })}
                    type="date"
                />
            )
        }
        default: {
            return (
                <TextInput
                    defaultValue={filterInternal.value?.toString()}
                    onValueChange={value =>
                        setFilterInternal({
                            value: selectedFilterType == 'number'
                                ? toNumberFilterValue(value ?? '', filterInternal.operator)
                                : value ?? '',
                        })
                    }
                />
            )
        }
    }
}

// `in` takes a comma separated list; an empty / invalid input stays '' so the filter is not applied
export const toNumberFilterValue = (value: string, operator?: TFilter['operator']): TFilter['value'] => {
    if (operator == 'in') {
        const numbers = value
            .split(',')
            .map(part => part.trim())
            .filter(part => part !== '')
            .map(Number)
            .filter(number => !Number.isNaN(number))
        return numbers.length ? numbers : ''
    }

    return value.trim() === '' || Number.isNaN(+value) ? '' : +value
}

export const generateAccessorColumn = <T,>(column: TableColumn<T>) =>
    column.accessor + (column.accessor2 ? `|${column.accessor2}` : '')

type TProps<T> = {
    filter: Partial<TFilter>
    columns: TableColumn<T>[]
    updateFilter: (filter: Partial<TFilter>) => void
    deleteFilter: () => void
}

function Filter<T>({ filter, columns, updateFilter, deleteFilter }: TProps<T>) {
    const tFilters = useTranslations('table.filters')
    const tOperators = useTranslations('table.filters.operators')

    const generateDefaultFilter = (): Partial<TFilter> => ({
        column: undefined,
        operator: undefined,
        value: '',
    })

    const getSelectedFilterType = (column: string) =>
        columns.find(c => generateAccessorColumn(c) == column)?.type ?? 'text'

    const [selectedFilterType, setSelectedFilterType] = useState(
        filter && filter.column ? getSelectedFilterType(filter.column) : 'text'
    )
    const [filterInternal, setFilterInternal] = useReducer(
        (current: Partial<TFilter>, update: Partial<TFilter>) => {
            return {
                ...current,
                ...update,
            }
        },
        filter ?? generateDefaultFilter()
    )

    // always pushed, also when the value was cleared: the popover applies only complete filters
    useUpdateEffect(() => {
        updateFilter(filterInternal)
    }, [filterInternal])

    // Inline elements (not nested components, which would remount the selects on every render).
    const columnsSelect = (
        <Select.Root
            value={filterInternal.column ?? ''}
            onValueChange={value => {
                // the operators and the value depend on the column type
                setSelectedFilterType(getSelectedFilterType(value))
                setFilterInternal({ column: value, operator: undefined, value: '' })
            }}
        >
            <Select.Trigger placeholder={tFilters('column-placeholder')} />
            <Select.Content>
                {columns.map((value, key) => (
                    <Select.Item key={key} value={generateAccessorColumn(value)}>
                        {value.label}
                    </Select.Item>
                ))}
            </Select.Content>
        </Select.Root>
    )

    const operatorsSelect = (
        <Select.Root
            // the value shape depends on the operator (between = range, in = list)
            onValueChange={value =>
                setFilterInternal({ operator: value as TFilter['operator'], value: '' })
            }
            value={filterInternal.operator ?? ''}
        >
            <Select.Trigger placeholder={tFilters('operator-placeholder')} />
            <Select.Content>
                {Operators[selectedFilterType].map(op => (
                    <Select.Item key={op.key} value={op.key}>
                        {tOperators(op.key)}
                    </Select.Item>
                ))}
            </Select.Content>
        </Select.Root>
    )

    return (
        <>
            {columnsSelect}
            {operatorsSelect}
            <RenderValueField
                // remount: the inputs are uncontrolled and their value was reset
                key={`${filterInternal.column}|${filterInternal.operator}`}
                selectedFilterType={selectedFilterType}
                filterInternal={filterInternal}
                setFilterInternal={setFilterInternal}
            />
            <IconButton color="crimson" onClick={deleteFilter} variant="soft">
                <FontAwesomeIcon icon={faRemove} />
            </IconButton>
        </>
    )
}

export default Filter
